import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Sparkles, FileSignature, Calendar, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useContract, useUpdateContract } from "@/hooks/useContracts";
import { saveContractVersion } from "@/hooks/useContractAnalytics";
import { ContractVersionHistory } from "@/components/contracts/ContractVersionHistory";
import { ContractApprovalWorkflow } from "@/components/contracts/ContractApprovalWorkflow";
import { SendForSignatureDialog } from "@/components/contracts/SendForSignatureDialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";


interface ContractContent {
  title?: string;
  executiveSummary?: string;
  scopeOfWork?: string;
  pricing?: string;
  termsAndConditions?: string;
  timeline?: string;
  signatures?: string;
}

export default function ContractViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: contract, isLoading } = useContract(id || "");
  const updateContract = useUpdateContract();

  const [title, setTitle] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [content, setContent] = useState<ContractContent>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);

  useEffect(() => {
    if (contract) {
      setTitle(contract.title || "");
      setSignerName(contract.signer_name || "");
      setSignerEmail(contract.signer_email || "");
      setExpiresAt(contract.expires_at || "");
      
      const metadata = contract.metadata as Record<string, unknown> | null;
      if (metadata?.generatedContent) {
        setContent(metadata.generatedContent as ContractContent);
      }
    }
  }, [contract]);

  const handleSave = async () => {
    if (!id || !contract) return;
    setIsSaving(true);
    
    try {
      const existingMetadata = (contract?.metadata as Record<string, unknown> || {});
      const metadata = {
        ...existingMetadata,
        generatedContent: content as Record<string, string>,
      };

      // Save version before updating
      await saveContractVersion(
        id,
        contract.tenant_id,
        {
          title,
          status: contract.status,
          metadata,
          signer_name: signerName,
          signer_email: signerEmail,
          expires_at: expiresAt || null,
        },
        `Updated contract: ${title !== contract.title ? 'title changed, ' : ''}${signerEmail !== contract.signer_email ? 'signer changed, ' : ''}content modified`
      );

      await updateContract.mutateAsync({
        id,
        title,
        signer_name: signerName,
        signer_email: signerEmail,
        expires_at: expiresAt || null,
        metadata,
      });
      toast.success("Contract saved and version recorded");
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["contracts", id] });
    queryClient.invalidateQueries({ queryKey: ["contracts"] });
  };

  const handleMarkSigned = async () => {
    if (!id) return;
    
    try {
      await updateContract.mutateAsync({
        id,
        status: "signed",
        signed_at: new Date().toISOString(),
      });
      
      // Send notification email
      await supabase.functions.invoke('contract-signed-notification', {
        body: { contractId: id },
      });
      
      toast.success("Contract marked as signed");
    } catch (error) {
      console.error("Mark signed error:", error);
    }
  };

  const statusColors: Record<string, string> = {
    draft: "bg-muted text-muted-foreground",
    sent: "bg-amber-500/20 text-amber-500",
    viewed: "bg-blue-500/20 text-blue-500",
    signed: "bg-green-500/20 text-green-500",
    expired: "bg-destructive/20 text-destructive",
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <FileSignature className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold">Contract not found</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/app/contracts")}>
          Back to Contracts
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/contracts")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{contract.title}</h1>
              <Badge className={statusColors[contract.status] || ""}>
                {contract.status.toUpperCase()}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              {contract.deals?.title ? `Deal: ${contract.deals.title}` : "No associated deal"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
          {contract.status === "draft" && 
           (!(contract as any).approval_required || (contract as any).approval_status === "approved") && (
            <Button onClick={() => setShowSendDialog(true)}>
              <Send className="h-4 w-4 mr-2" />
              Send for Signature
            </Button>
          )}
          {(contract.status === "sent" || contract.status === "viewed") && (
            <Button onClick={handleMarkSigned} className="bg-green-600 hover:bg-green-700">
              <FileSignature className="h-4 w-4 mr-2" />
              Mark as Signed
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contract Content Editor */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Contract Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Contract Title</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter contract title"
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Executive Summary</Label>
                <Textarea
                  value={content.executiveSummary || ""}
                  onChange={(e) => setContent({ ...content, executiveSummary: e.target.value })}
                  placeholder="Brief overview of the agreement..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Scope of Work</Label>
                <Textarea
                  value={content.scopeOfWork || ""}
                  onChange={(e) => setContent({ ...content, scopeOfWork: e.target.value })}
                  placeholder="Detailed description of services/products..."
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label>Pricing & Payment Terms</Label>
                <Textarea
                  value={content.pricing || ""}
                  onChange={(e) => setContent({ ...content, pricing: e.target.value })}
                  placeholder="Pricing breakdown and payment schedule..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Terms and Conditions</Label>
                <Textarea
                  value={content.termsAndConditions || ""}
                  onChange={(e) => setContent({ ...content, termsAndConditions: e.target.value })}
                  placeholder="Standard terms including warranties, liability..."
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label>Timeline & Milestones</Label>
                <Textarea
                  value={content.timeline || ""}
                  onChange={(e) => setContent({ ...content, timeline: e.target.value })}
                  placeholder="Project timeline and key milestones..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contract Details */}
          <Card>
            <CardHeader>
              <CardTitle>Contract Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Signer Name</Label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="John Smith"
                />
              </div>

              <div className="space-y-2">
                <Label>Signer Email</Label>
                <Input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="client@company.com"
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Expiration Date
                </Label>
                <Input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for no expiration
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Status Timeline */}
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Created</p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(contract.created_at), "MMM d, yyyy h:mm a")}
                  </p>
                </div>
              </div>
              
              {contract.sent_at && (
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-amber-500" />
                  <div>
                    <p className="text-sm font-medium">Sent</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(contract.sent_at), "MMM d, yyyy h:mm a")}
                    </p>
                  </div>
                </div>
              )}
              
              {contract.viewed_at && (
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                  <div>
                    <p className="text-sm font-medium">Viewed</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(contract.viewed_at), "MMM d, yyyy h:mm a")}
                    </p>
                  </div>
                </div>
              )}
              
              {contract.signed_at && (
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <div>
                    <p className="text-sm font-medium">Signed</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(contract.signed_at), "MMM d, yyyy h:mm a")}
                    </p>
                  </div>
                </div>
              )}

              {contract.expires_at && !contract.signed_at && (
                <div className="flex items-center gap-3">
                  <div className={`h-2 w-2 rounded-full ${new Date(contract.expires_at) < new Date() ? 'bg-destructive' : 'bg-amber-500'}`} />
                  <div>
                    <p className="text-sm font-medium">
                      {new Date(contract.expires_at) < new Date() ? 'Expired' : 'Expires'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(contract.expires_at), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Deal Info */}
          {contract.deals && (
            <Card>
              <CardHeader>
                <CardTitle>Associated Deal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Title</span>
                  <span className="font-medium">{contract.deals.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">
                    ${contract.deals.amount?.toLocaleString() || 0}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Approval Workflow */}
          <ContractApprovalWorkflow
            contractId={id!}
            contractStatus={contract.status}
            approvalRequired={(contract as any).approval_required}
            approvalStatus={(contract as any).approval_status}
          />

          {/* Version History */}
          <ContractVersionHistory 
            contractId={id!} 
            currentVersion={contract.version}
          />
        </div>
      </div>

      <SendForSignatureDialog
        open={showSendDialog}
        onOpenChange={setShowSendDialog}
        contractId={id!}
        contractTitle={contract.title}
        initialSignerName={signerName}
        initialSignerEmail={signerEmail}
        onSuccess={handleSendSuccess}
      />
    </div>
  );
}
